import { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import axios from 'axios';
import Moment from 'moment';
// material
import { styled } from '@mui/material/styles';
import {
  Card,
  Stack,
  CircularProgress,
  Container,
  Typography,
  OutlinedInput,
  Box,
  TextField,
  MenuItem,
  Avatar,
  Grid,
} from '@mui/material';
import { RemoveRedEye } from '@mui/icons-material';
import CopyToClipboard from 'react-copy-to-clipboard';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import 'react-toastify/dist/ReactToastify.css';
import { toast } from 'react-toastify';
// import Filter from '../components/filters/Qtos';

// components
import PageHeader from '../components/smallComponents/PageHeader';
import Scrollbar from '../components/Scrollbar';
import Iconify from '../components/Iconify';
import Page from '../components/Page';

import ProjectMaterialTransferLogs from '../components/ProjectMaterialTransferLogs';

const SearchStyle = styled(OutlinedInput)(({ theme }) => ({
  width: 240,
  transition: theme.transitions.create(['box-shadow', 'width'], {
    easing: theme.transitions.easing.easeInOut,
    duration: theme.transitions.duration.shorter,
  }),
  '&.Mui-focused': { width: 320, boxShadow: theme.customShadows.z8 },
  '& fieldset': {
    borderWidth: `1px !important`,
    borderColor: `${theme.palette.grey[500_32]} !important`,
  },
}));

function DetailItem({ icon, title, data, background, sx, onClick, profilePic, statusChange, onStatusChange }) {
  return (
    <Stack
      alignItems="center"
      spacing={1}
      direction="row"
      sx={{
        height: '100%',
        background: (theme) => theme.palette.macinsIndigo.lighter,
        minHeight: '65px',
        borderRadius: '5px',
        ...sx,
      }}
      onClick={onClick}
      paddingX={1}
      paddingY={0.5}
    >
      <Stack direction="row" alignItems="center" background={background} padding={profilePic ? 0 : 1}>
        {profilePic && (
          <Avatar src={profilePic} alt={title} style={{ height: '100px', width: '100px' }} color="primary" />
        )}
        {!profilePic && (
          <Iconify icon={icon} sx={{ height: '40px', width: '40px', color: (theme) => theme.palette.primary.main }} />
        )}
      </Stack>

      {statusChange === 'yes' ? (
        <>
          <TextField select label="Status" value={data} size="small" onChange={(e) => onStatusChange(e.target.value)}>
            <MenuItem value="false">Active</MenuItem>
            <MenuItem value="true">Blocked</MenuItem>
          </TextField>
        </>
      ) : (
        <Stack direction="column">
          <Typography style={{ fontSize: '16px', fontWeight: 'bold' }}>{title}</Typography>
          <Typography style={{ wordBreak: 'break-all' }}>{data}</Typography>
        </Stack>
      )}
    </Stack>
  );
}

export default function MaterialProjectLogs() {
  const [searchParams, setSearchParams] = useSearchParams();
  const projectId = searchParams.get('projectId');
  const materialId = searchParams.get('materialId');
  const [isLoading, setIsLoading] = useState(true);
  const [material, setMaterial] = useState({});
  const [materialUsage, setMaterialUsage] = useState({});
  const [project, setProject] = useState({});

  const getMaterialUsageData = async () => {
    try {
      const res = await axios.get(`${process.env.REACT_APP_API_URL}/materialFlowLog/usageInProject/single`, {
        params: { materialId, projectId },
      });
      console.log(res);
      if (res.data.isError === false) {
        setMaterialUsage(res.data.data);
        setMaterial(res.data.data.materialData);
        setProject(res.data.data.projectData);
      }
      setIsLoading(false);
    } catch (error) {
      console.log(error);
    }
  };

  const handleStatusChange = async (status) => {
    try {
      const res = await axios.put(`${process.env.REACT_APP_API_URL}/material/status`, {
        materialId: material._id,
        status,
      });
      console.log(res);
      if (res.data.isError === false) {
        toast.success(res.data.message);
        getMaterialUsageData();
      }
    } catch (error) {
      console.log(error);
      toast.error(error.response.data.message);
    }
  };

  useEffect(() => {
    getMaterialUsageData();
  }, []);

  return (
    <Page title="Project Material Details">
      {isLoading ? (
        <div
          style={{
            width: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: '50vh',
          }}
        >
          <CircularProgress />
        </div>
      ) : (
        <Container maxWidth="xl">
          <PageHeader title="Project Material Details" />
          <Stack direction={'column'} spacing={2}>
            <Card>
              <Box padding={2}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <DetailItem
                      icon="icons8:parallel-tasks"
                      title="Project Name"
                      data={materialUsage.projectData.name}
                      background="#f0f0f0"
                      sx={{ minHeight: '65px' }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <DetailItem
                      icon="lets-icons:materials"
                      title="Material Name"
                      data={material.name}
                      background="#f0f0f0"
                      sx={{ minHeight: '65px' }}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <DetailItem
                      icon="emojione-monotone:letter-u"
                      title="Unit"
                      data={material.unit}
                      background="#f0f0f0"
                      sx={{ minHeight: '65px' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <DetailItem
                      icon="ion:pricetags"
                      title="Unit Price"
                      data={material.unitPrice}
                      background="#f0f0f0"
                      sx={{ minHeight: '65px' }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <DetailItem
                      icon="teenyicons:spreadsheet-solid"
                      title="Total Used"
                      data={materialUsage.totalQuantity}
                      background="#f0f0f0"
                      sx={{ minHeight: '65px' }}
                    />
                  </Grid>
                  {/* <Grid item xs={12} sm={6} md={4}>
                    <DetailItem
                      icon={material.isBlocked ? 'icomoon-free:blocked' : 'gg:check-o'}
                      title="Status"
                      data={material.isBlocked ? 'Blocked' : 'Active'}
                      background="#f0f0f0"
                      sx={{ minHeight: '65px' }}
                      statusChange={'yes'}
                      onStatusChange={handleStatusChange}
                    />
                  </Grid> */}
                  <Grid item xs={12}>
                    <DetailItem
                      icon="material-symbols:description"
                      title="Description"
                      data={material.description}
                      background="#f0f0f0"
                      sx={{ minHeight: '65px' }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Card>
            <ProjectMaterialTransferLogs material={material} getMaterialUsageData={getMaterialUsageData} />
          </Stack>
        </Container>
      )}
    </Page>
  );
}
